import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CheckCircle, Clock, Warning, UploadSimple, FileText, Copy, Info } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = {
  draft: { l: "Brouillon", c: "bg-muted text-muted-foreground" },
  submitted: { l: "Reçue · en attente de vérification", c: "bg-primary/10 text-primary" },
  under_review: { l: "En cours d'étude", c: "bg-accent/10 text-accent" },
  accepted: { l: "Admis 🎉", c: "bg-secondary text-secondary-foreground" },
  conditional: { l: "Admission conditionnelle", c: "bg-accent/20 text-accent" },
  waitlisted: { l: "Liste d'attente", c: "bg-accent/10 text-accent" },
  rejected: { l: "Refusée", c: "bg-destructive/10 text-destructive" },
  archived: { l: "Archivée", c: "bg-muted text-muted-foreground" },
};
const DOC_ST = {
  pending: { l: "À vérifier", c: "text-muted-foreground", icon: Clock },
  verified: { l: "Vérifié", c: "text-secondary", icon: CheckCircle },
  refused: { l: "Refusé", c: "text-destructive", icon: Warning },
};
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx";

export default function PublicApplicantPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const fileRefs = useRef({});

  const load = async () => {
    try {
      const r = await axios.get(`${API}/public/applications/track/${token}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Candidature introuvable"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line

  const uploadFor = async (key, label, file) => {
    if (file.size > 20 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 20 Mo)");
      return;
    }
    setUploading(key);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej;
        r.readAsDataURL(file);
      });
      // Single public call: upload + attach in one shot (no auth needed).
      await axios.post(`${API}/public/applications/track/${token}/documents`, {
        key, label, data_url: b64, filename: file.name,
      });
      toast.success(`${label} téléversé`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec du téléversement"); }
    finally { setUploading(null); }
  };

  if (loading) return <div className="p-10 text-center text-muted-foreground">Chargement…</div>;
  if (!data) return <div className="p-10 text-center text-destructive">Candidature introuvable ou lien invalide.</div>;

  const { application: a, school: s, requirements: reqs } = data;
  const submittedDocsByKey = Object.fromEntries((a.documents || []).map(d => [d.key, d]));
  const status = STATUS[a.status] || STATUS.submitted;

  // Merge: required docs first, then any extra uploaded docs
  const requiredKeys = reqs.map(r => r.document_key);
  const extraDocs = (a.documents || []).filter(d => !requiredKeys.includes(d.key));

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b" style={{ background: s.primary_color || "#1a2a6c", color: "white" }}>
        <div className="max-w-4xl mx-auto p-6 flex items-center gap-4">
          {s.logo_url && <img src={s.logo_url.startsWith("http") ? s.logo_url : `${process.env.REACT_APP_BACKEND_URL}${s.logo_url}`} alt="" className="w-14 h-14 rounded bg-white/20 object-contain" />}
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest opacity-70">{s.name}</div>
            <h1 className="font-heading text-2xl font-bold">Suivi de candidature</h1>
          </div>
        </div>
      </header>
      <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-10 space-y-6">
        <Card className="p-5">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs text-muted-foreground">Numéro de candidature</div>
              <div className="flex items-center gap-2">
                <div className="font-mono text-xl font-bold">{a.application_number || a.id.slice(0, 8)}</div>
                <button className="text-muted-foreground hover:text-primary" title="Copier"
                        onClick={() => { navigator.clipboard.writeText(a.application_number || a.id); toast.success("Copié"); }}>
                  <Copy size={14} />
                </button>
              </div>
              <div className="mt-2 text-sm">
                <b>{a.child_first_name} {a.child_last_name}</b> · {a.grade_level_name || a.grade_level_id || "—"}
              </div>
            </div>
            <Badge className={`${status.c} text-sm px-3 py-1`} data-testid="status-badge">{status.l}</Badge>
          </div>
          {a.decision_note && (
            <div className="mt-3 text-sm bg-muted/40 rounded p-3 border-l-2 border-primary flex gap-2">
              <Info size={14} className="shrink-0 mt-0.5" />
              <div><b>Note de l'établissement :</b> {a.decision_note}</div>
            </div>
          )}
        </Card>

        <Card className="p-5">
          <h2 className="font-heading font-semibold mb-3 flex items-center gap-2">
            <FileText size={20} weight="duotone" className="text-primary" /> Pièces à fournir
          </h2>
          <div className="space-y-2">
            {reqs.length === 0 && extraDocs.length === 0 && (
              <div className="text-sm text-muted-foreground italic">Aucun document requis configuré pour cette école.</div>
            )}
            {reqs.map(r => {
              const d = submittedDocsByKey[r.document_key];
              const st = d ? (DOC_ST[d.status] || DOC_ST.pending) : null;
              const Icon = st?.icon;
              return (
                <div key={r.id} className="border rounded-lg p-3" data-testid={`req-${r.document_key}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium">
                        {r.label}
                        {r.obligatory ? <span className="ml-2 text-[9px] uppercase bg-accent/10 text-accent px-1.5 py-0.5 rounded">Obligatoire</span> :
                                        <span className="ml-2 text-[9px] uppercase bg-muted px-1.5 py-0.5 rounded">Facultatif</span>}
                      </div>
                      {d ? (
                        <div className="text-xs mt-1 flex items-center gap-1.5">
                          <Icon size={12} className={st.c} weight="duotone" />
                          <span className={st.c}>{st.l}</span>
                          <span className="text-muted-foreground">· <a href={d.url?.startsWith("http") ? d.url : `${process.env.REACT_APP_BACKEND_URL}${d.url}`} target="_blank" rel="noreferrer" className="underline">{d.filename}</a></span>
                        </div>
                      ) : (
                        <div className="text-xs text-muted-foreground mt-1">Non fourni</div>
                      )}
                      {d?.status === "refused" && d.refusal_reason && (
                        <div className="text-xs bg-destructive/5 text-destructive rounded p-2 mt-2 border-l-2 border-destructive">
                          <b>Motif du refus :</b> {d.refusal_reason} — Merci de téléverser une nouvelle version.
                        </div>
                      )}
                    </div>
                    {(!d || d.status !== "verified") && a.status !== "archived" && a.status !== "rejected" && (
                      <>
                        <input type="file" accept={ACCEPT}
                               ref={el => (fileRefs.current[r.document_key] = el)}
                               className="hidden"
                               onChange={e => { const f = e.target.files?.[0]; if (f) uploadFor(r.document_key, r.label, f); e.target.value = ""; }} />
                        <Button size="sm" variant="outline" disabled={uploading === r.document_key}
                                onClick={() => fileRefs.current[r.document_key]?.click()}
                                data-testid={`btn-upload-${r.document_key}`}>
                          <UploadSimple size={14} className="mr-1" />
                          {d ? "Remplacer" : "Téléverser"}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
            {extraDocs.length > 0 && (
              <div className="pt-2 mt-2 border-t">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Autres documents fournis</div>
                {extraDocs.map((d, i) => {
                  const st = DOC_ST[d.status] || DOC_ST.pending;
                  const Icon = st.icon;
                  return (
                    <div key={i} className="text-xs flex items-center gap-2 py-1">
                      <Icon size={12} className={st.c} weight="duotone" />
                      <a href={d.url?.startsWith("http") ? d.url : `${process.env.REACT_APP_BACKEND_URL}${d.url}`} target="_blank" rel="noreferrer" className="underline flex-1 truncate">{d.filename || d.label}</a>
                      <span className={`text-[10px] ${st.c}`}>{st.l}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>

        <div className="text-center text-xs text-muted-foreground">
          Conservez ce lien précieusement — il permet de suivre votre candidature à tout moment.
          <br />La réception d'une candidature ne signifie pas automatiquement que l'admission est acceptée.
        </div>
      </div>
    </div>
  );
}
